import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useMuscleGroups from '../../hooks/useMuscleGroups';
import MuscleGroupSelector from '../common/MuscleGroupSelector';
import { 
    Image, Loader2, AlertCircle, CheckCircle, Upload, Save, 
    ChevronLeft, ChevronRight, Play, Pause, Scissors, X, Search
} from 'lucide-react';

const ExerciseImageEditor = () => {
    const { getAuthenticatedApi } = useAuth();
    const api = getAuthenticatedApi();
    const { muscles, loading: musclesLoading, getMusclesByCategory } = useMuscleGroups();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [activeTab, setActiveTab] = useState('editor'); // 'editor' o 'frames'
    
    // Stato per GIF e frame
    const [gifUrl, setGifUrl] = useState(null);
    const [gifLoading, setGifLoading] = useState(false);
    const [frames, setFrames] = useState([]);
    const [framesLoading, setFramesLoading] = useState(false);
    const [selectedFrames, setSelectedFrames] = useState([]);
    const [frameCropAreas, setFrameCropAreas] = useState({});
    const [previewUrl, setPreviewUrl] = useState(null);
    const [generatingPreview, setGeneratingPreview] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // Stato per form dati esercizio
    const [formData, setFormData] = useState({
        nome: '',
        nome_en: '',
        descrizione: '',
        descrizione_en: '',
        gruppo_muscolare: '',
        primary_muscle_id: null,
        attrezzatura: '',
        is_cardio: 0,
        immagine_nome: '',
        immagine_statica: ''
    });
    const [selectedCategory, setSelectedCategory] = useState('');
    const [secondaryMuscles, setSecondaryMuscles] = useState([]);
    const [modifiedFields, setModifiedFields] = useState(new Set());
    const [selectedEquipment, setSelectedEquipment] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Categorie muscolari
    const categories = ['Gambe', 'Petto', 'Schiena', 'Spalle', 'Braccia', 'Core', 'Cardio'];
    
    // Refs per crop
    const cropRefs = useRef({});
    const isSelectingRef = useRef(false);
    const startPosRef = useRef({ x: 0, y: 0 });

    // Ottieni l'URL base dell'API
    const getApiBaseUrl = () => {
        return process.env.REACT_APP_API_BASE_URL || '/api';
    };

    // Stato per tutti gli esercizi (prima del filtro)
    const [allExercises, setAllExercises] = useState([]);

    // Carica gli esercizi
    const fetchExercises = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await api.get('/esercizi.php?legacy=true');
            const allExercisesData = Array.isArray(response.data) ? response.data : [];
            
            const pendingExercises = allExercisesData.filter(ex => {
                const statusMatch = ex.status === 'pending_review';
                const userMatch = ex.created_by_user_id == 17 || ex.created_by_user_id === '17' || parseInt(ex.created_by_user_id) === 17;
                const hasImage = !!ex.immagine_nome;
                return statusMatch && userMatch && hasImage;
            });
            
            setAllExercises(pendingExercises);
            return pendingExercises;
        } catch (err) {
            console.error('Errore nel caricamento degli esercizi:', err);
            setError('Errore durante il caricamento degli esercizi. Riprova più tardi.');
            return [];
        } finally {
            setLoading(false);
        }
    };

    // Calcola le attrezzature disponibili dagli esercizi
    const availableEquipment = React.useMemo(() => {
        const equipmentSet = new Set();
        allExercises.forEach(ex => {
            if (ex.attrezzatura && ex.attrezzatura.trim() !== '') {
                equipmentSet.add(ex.attrezzatura.trim());
            }
        });
        return Array.from(equipmentSet).sort();
    }, [allExercises]);

    // Filtra gli esercizi in base all'attrezzatura selezionata e al termine di ricerca
    const exercises = React.useMemo(() => {
        let filtered = allExercises;
        
        // Filtro per attrezzatura
        if (selectedEquipment && selectedEquipment !== '') {
            filtered = filtered.filter(ex => ex.attrezzatura && ex.attrezzatura.trim() === selectedEquipment);
        }
        
        // Filtro per nome (ricerca case-insensitive)
        if (searchTerm && searchTerm.trim() !== '') {
            const searchLower = searchTerm.trim().toLowerCase();
            filtered = filtered.filter(ex => {
                const nome = (ex.nome || '').toLowerCase();
                const nomeEn = (ex.nome_en || '').toLowerCase();
                return nome.includes(searchLower) || nomeEn.includes(searchLower);
            });
        }
        
        return filtered;
    }, [allExercises, selectedEquipment, searchTerm]);

    // Carica un esercizio specifico
    const loadExercise = (exercise) => {
        setSelectedExercise(exercise);
        setCurrentIndex(exercises.findIndex(e => e.id === exercise.id));
        
        // Reset form
        setFormData({
            nome: exercise.nome || '',
            nome_en: exercise.nome_en || exercise.nome || '',
            descrizione: exercise.descrizione || '',
            descrizione_en: exercise.descrizione_en || '',
            gruppo_muscolare: exercise.gruppo_muscolare || '',
            primary_muscle_id: exercise.primary_muscle_id || null,
            attrezzatura: exercise.attrezzatura || '',
            is_cardio: exercise.is_cardio || 0,
            immagine_nome: exercise.immagine_nome || '',
            immagine_statica: exercise.immagine_statica || ''
        });
        
        // Imposta categoria in base al muscolo primario
        // Prova prima con primary_muscle (se disponibile dall'API)
        if (exercise.primary_muscle && exercise.primary_muscle.category) {
            setSelectedCategory(exercise.primary_muscle.category);
        } else if (exercise.primary_muscle_id) {
            // Se i muscoli sono già caricati, usa quelli
            const muscle = muscles.find(m => m.id === exercise.primary_muscle_id);
            if (muscle && muscle.parent_category) {
                setSelectedCategory(muscle.parent_category);
            } else if (exercise.gruppo_muscolare) {
                // Fallback: usa gruppo_muscolare se il muscolo non è ancora caricato
                setSelectedCategory(exercise.gruppo_muscolare);
            }
        } else if (exercise.gruppo_muscolare) {
            setSelectedCategory(exercise.gruppo_muscolare);
        } else {
            // Se non c'è nulla, resetta la categoria
            setSelectedCategory('');
        }
        
        // Carica muscoli secondari dalla tabella exercise_muscles
        const loadSecondaryMuscles = async () => {
            if (exercise.secondary_muscles && Array.isArray(exercise.secondary_muscles) && exercise.secondary_muscles.length > 0) {
                // Estrai i muscoli secondari con activation_level (formato: [{id: X, name: "...", activation_level: "..."}, ...])
                const secondaryMusclesData = exercise.secondary_muscles
                    .map(m => {
                        const muscleId = m.id || m.muscle_group_id || m.muscle_id || (typeof m === 'number' ? m : null);
                        if (muscleId === null || muscleId === undefined || muscleId === '') return null;
                        return {
                            muscle_id: muscleId,
                            activation_level: m.activation_level || 'medium'
                        };
                    })
                    .filter(m => m !== null);
                
                setSecondaryMuscles(secondaryMusclesData);
            } else {
                // Se non ci sono muscoli secondari nell'esercizio, caricali direttamente dalla tabella exercise_muscles
                try {
                    const musclesResponse = await api.get(`/exercise_muscles.php?exercise_id=${exercise.id}&is_primary=0`);
                    if (musclesResponse.data && musclesResponse.data.success && musclesResponse.data.data) {
                        const secondaryMusclesData = musclesResponse.data.data
                            .map(m => ({
                                muscle_id: m.muscle_group_id || m.muscle_id,
                                activation_level: m.activation_level || 'medium'
                            }))
                            .filter(m => m.muscle_id !== null && m.muscle_id !== undefined);
                        setSecondaryMuscles(secondaryMusclesData);
                    } else {
                        setSecondaryMuscles([]);
                    }
                } catch (err) {
                    console.warn('Errore nel caricamento dei muscoli secondari:', err);
                    setSecondaryMuscles([]);
                }
            }
        };
        
        loadSecondaryMuscles();
        
        // Carica GIF
        if (exercise.immagine_nome) {
            const apiBaseUrl = getApiBaseUrl();
            const gifUrl = `${apiBaseUrl}/serve_exercise_gif.php?filename=${encodeURIComponent(exercise.immagine_nome)}`;
            setGifUrl(gifUrl);
        }
        
        // Reset frame
        setFrames([]);
        setSelectedFrames([]);
        setFrameCropAreas({});
        setPreviewUrl(null);
    };

    // Carica frame dalla GIF
    const loadFrames = async () => {
        if (!selectedExercise || !selectedExercise.immagine_nome) {
            setError('Nessun esercizio selezionato o immagine mancante');
            return;
        }
        
        try {
            setFramesLoading(true);
            const apiBaseUrl = getApiBaseUrl();
            const response = await api.get(
                `/generate_exercise_static_image.php?action=get_frames&filename=${encodeURIComponent(selectedExercise.immagine_nome)}`
            );
            
            if (response.data.success) {
                // Costruisci URL completi per i frame
                const framesWithFullUrl = response.data.frames.map(frame => ({
                    ...frame,
                    url: `${apiBaseUrl}${frame.url}`
                }));
                setFrames(framesWithFullUrl);
                setError(null);
            } else {
                setError(response.data.message || 'Errore nel caricamento dei frame');
            }
        } catch (err) {
            console.error('Errore nel caricamento dei frame:', err);
            setError(err.response?.data?.message || 'Errore durante il caricamento dei frame');
        } finally {
            setFramesLoading(false);
        }
    };

    // Toggle selezione frame
    const toggleFrameSelection = (frameIndex) => {
        setSelectedFrames(prev => {
            const index = prev.indexOf(frameIndex);
            if (index > -1) {
                return prev.filter(f => f !== frameIndex);
            } else {
                if (prev.length < 3) {
                    return [...prev, frameIndex].sort((a, b) => a - b);
                } else {
                    setError('Puoi selezionare massimo 3 frame');
                    return prev;
                }
            }
        });
    };

    // Genera anteprima
    const generatePreview = async () => {
        if (selectedFrames.length < 2) {
            setError('Seleziona almeno 2 frame');
            return;
        }
        
        try {
            setGeneratingPreview(true);
            
            const params = {
                action: 'generate_preview',
                filename: selectedExercise.immagine_nome,
                frames: selectedFrames.join(','),
                layout: 'horizontal',
                background: 'white',
                crop_data: JSON.stringify(frameCropAreas)
            };
            
            const response = await api.get('/generate_exercise_static_image.php', { params });
            
            if (response.data.success) {
                // Costruisci URL completo per l'anteprima
                const apiBaseUrl = getApiBaseUrl();
                setPreviewUrl(`${apiBaseUrl}${response.data.url}`);
            } else {
                setError(response.data.message || 'Errore nella generazione dell\'anteprima');
            }
        } catch (err) {
            console.error('Errore nella generazione dell\'anteprima:', err);
            setError(err.response?.data?.message || 'Errore durante la generazione dell\'anteprima');
        } finally {
            setGeneratingPreview(false);
        }
    };

    // Genera e salva immagine statica
    const generateAndSaveStaticImage = async () => {
        if (selectedFrames.length < 2) {
            setError('Seleziona almeno 2 frame');
            return;
        }
        
        if (!selectedExercise) {
            setError('Nessun esercizio selezionato');
            return;
        }
        
        try {
            setSaving(true);
            
            // Genera nome file
            const baseName = selectedExercise.immagine_nome.replace(/\.gif$/i, '');
            const outputName = baseName + '.png';
            
            // Converti crop_data in stringa JSON come si aspetta il PHP
            const response = await api.post('/generate_exercise_static_image.php', {
                action: 'generate_and_save',
                exercise_id: selectedExercise.id,
                filename: selectedExercise.immagine_nome,
                frames: selectedFrames.join(','),
                layout: 'horizontal',
                background: 'white',
                crop_data: JSON.stringify(frameCropAreas),
                output_name: outputName
            });
            
            if (response.data.success) {
                // Aggiorna formData con il nuovo nome immagine statica (preservando tutte le altre modifiche)
                setFormData(prev => ({
                    ...prev,
                    immagine_statica: outputName
                }));
                setModifiedFields(prev => new Set([...prev, 'immagine_statica']));
                
                // Aggiorna anche selectedExercise per riflettere il cambiamento nel DB
                setSelectedExercise(prev => ({
                    ...prev,
                    immagine_statica: outputName
                }));
                
                // Aggiorna la lista degli esercizi in background (senza resettare il form)
                fetchExercises().catch(err => {
                    console.warn('Errore nell\'aggiornamento della lista esercizi:', err);
                });
                
                // Resetta l'anteprima e passa alla tab Editor Dati
                setPreviewUrl(null);
                setError(null);
                setActiveTab('editor');
            } else {
                setError(response.data.message || 'Errore nella generazione dell\'immagine statica');
            }
        } catch (err) {
            console.error('Errore nella generazione dell\'immagine statica:', err);
            setError(err.response?.data?.message || 'Errore durante la generazione dell\'immagine statica');
        } finally {
            setSaving(false);
        }
    };

    // Salva modifiche esercizio
    const saveExerciseChanges = async () => {
        if (!selectedExercise || modifiedFields.size === 0) {
            setError('Nessuna modifica da salvare');
            return;
        }
        
        // Verifica che l'immagine statica sia presente
        const currentStaticImage = formData.immagine_statica || selectedExercise.immagine_statica;
        if (!currentStaticImage || currentStaticImage.trim() === '') {
            setError('Impossibile salvare: l\'immagine statica deve essere generata prima. Vai alla tab "Selezione Frame" per generarla.');
            return;
        }
        
        // Verifica che il muscolo primario sia presente
        const currentPrimaryMuscle = formData.primary_muscle_id || selectedExercise.primary_muscle_id;
        if (!currentPrimaryMuscle || currentPrimaryMuscle === null || currentPrimaryMuscle === '') {
            setError('Impossibile salvare: il muscolo primario deve essere selezionato. Vai alla tab "Editor Dati" per selezionarlo.');
            return;
        }
        
        try {
            setSaving(true);
            
            // Prepara dati da salvare - SEMPRE tutti i campi, anche se non modificati
            // Usa formData se disponibile, altrimenti usa selectedExercise come fallback
            const getValue = (field) => {
                // Se il campo è stato modificato o è presente in formData, usalo
                if (formData[field] !== undefined && formData[field] !== null) {
                    return formData[field];
                }
                // Altrimenti usa il valore da selectedExercise
                if (selectedExercise[field] !== undefined && selectedExercise[field] !== null) {
                    return selectedExercise[field];
                }
                // Valori di default per tipo
                if (field === 'is_cardio' || field === 'primary_muscle_id') {
                    return field === 'is_cardio' ? 0 : null;
                }
                return '';
            };
            
            // Includi SEMPRE tutti i campi
            const updateData = {
                nome: getValue('nome'),
                nome_en: getValue('nome_en'),
                descrizione: getValue('descrizione'),
                descrizione_en: getValue('descrizione_en'),
                gruppo_muscolare: getValue('gruppo_muscolare'),
                attrezzatura: getValue('attrezzatura'),
                immagine_nome: getValue('immagine_nome'),
                immagine_statica: getValue('immagine_statica'),
                is_cardio: getValue('is_cardio'),
                primary_muscle_id: getValue('primary_muscle_id')
            };
            
            // Salva campi base dell'esercizio
            if (Object.keys(updateData).length > 0) {
                const response = await api.put(`/esercizi.php?id=${selectedExercise.id}`, updateData);
                
                if (response.data && response.data.success === false) {
                    throw new Error(response.data.message || 'Errore nel salvataggio');
                }
            }
            
            // Gestisci muscolo primario nella tabella exercise_muscles se modificato
            if (modifiedFields.has('primary_muscle_id')) {
                try {
                    // Recupera le relazioni muscoli esistenti
                    const existingMuscles = await api.get(`/exercise_muscles.php?exercise_id=${selectedExercise.id}`);
                    if (existingMuscles.data && existingMuscles.data.success && existingMuscles.data.data) {
                        // Trova il muscolo primario esistente
                        const existingPrimary = existingMuscles.data.data.find(m => m.is_primary);
                        
                        if (formData.primary_muscle_id) {
                            // Se c'è un nuovo muscolo primario
                            if (existingPrimary) {
                                // Se esiste già un primario, verifica se è lo stesso
                                if (existingPrimary.muscle_group_id !== formData.primary_muscle_id) {
                                    // Elimina il vecchio primario
                                    await api.delete(`/exercise_muscles.php?id=${existingPrimary.id}`);
                                    // Inserisci il nuovo primario
                                    await api.post('/exercise_muscles.php', {
                                        exercise_id: selectedExercise.id,
                                        muscle_group_id: formData.primary_muscle_id,
                                        is_primary: true,
                                        activation_level: 'high'
                                    });
                                }
                            } else {
                                // Non c'era un primario, inseriscilo
                                await api.post('/exercise_muscles.php', {
                                    exercise_id: selectedExercise.id,
                                    muscle_group_id: formData.primary_muscle_id,
                                    is_primary: true,
                                    activation_level: 'high'
                                });
                            }
                        } else {
                            // Se primary_muscle_id è null, elimina il primario esistente
                            if (existingPrimary) {
                                await api.delete(`/exercise_muscles.php?id=${existingPrimary.id}`);
                            }
                        }
                    } else if (formData.primary_muscle_id) {
                        // Non ci sono relazioni esistenti, inserisci il primario
                        await api.post('/exercise_muscles.php', {
                            exercise_id: selectedExercise.id,
                            muscle_group_id: formData.primary_muscle_id,
                            is_primary: true,
                            activation_level: 'high'
                        });
                    }
                } catch (err) {
                    console.error('Errore nella gestione del muscolo primario:', err);
                }
            }
            
            // Gestisci muscoli secondari se modificati
            if (modifiedFields.has('secondary_muscles')) {
                // Prima elimina tutti i muscoli secondari esistenti per questo esercizio
                try {
                    const existingMuscles = await api.get(`/exercise_muscles.php?exercise_id=${selectedExercise.id}`);
                    if (existingMuscles.data && existingMuscles.data.success && existingMuscles.data.data) {
                        const secondaryMusclesToDelete = existingMuscles.data.data
                            .filter(m => !m.is_primary)
                            .map(m => m.id);
                        
                        // Elimina ogni muscolo secondario
                        for (const muscleRelationId of secondaryMusclesToDelete) {
                            await api.delete(`/exercise_muscles.php?id=${muscleRelationId}`);
                        }
                    }
                } catch (err) {
                    console.warn('Errore nell\'eliminazione dei muscoli secondari esistenti:', err);
                }
                
                // Poi inserisci i nuovi muscoli secondari
                for (const muscleData of secondaryMuscles) {
                    try {
                        await api.post('/exercise_muscles.php', {
                            exercise_id: selectedExercise.id,
                            muscle_group_id: muscleData.muscle_id,
                            is_primary: false,
                            activation_level: muscleData.activation_level || 'medium'
                        });
                    } catch (err) {
                        // Se la relazione esiste già, ignora l'errore
                        if (err.response?.data?.message?.includes('esiste già')) {
                            console.warn(`Muscolo secondario ${muscleData.muscle_id} già presente, ignorato`);
                        } else {
                            console.error(`Errore nell'inserimento del muscolo secondario ${muscleData.muscle_id}:`, err);
                        }
                    }
                }
            }
            
            setModifiedFields(new Set());
            
            // Ricarica la lista degli esercizi (l'esercizio salvato non dovrebbe più apparire
            // perché lo status è cambiato e created_by_user_id è stato rimosso)
            const updatedExercises = await fetchExercises();
            
            // Rimuovi l'esercizio dalla visualizzazione
            setSelectedExercise(null);
            setCurrentIndex(0);
            
            // Se ci sono altri esercizi nella lista, seleziona il primo
            if (updatedExercises && updatedExercises.length > 0) {
                loadExercise(updatedExercises[0]);
            }
            
            setError(null);
        } catch (err) {
            console.error('Errore nel salvataggio:', err);
            setError(err.response?.data?.message || 'Errore durante il salvataggio delle modifiche');
        } finally {
            setSaving(false);
        }
    };

    // Navigazione esercizi
    const navigateExercise = (direction) => {
        const newIndex = currentIndex + direction;
        if (newIndex >= 0 && newIndex < exercises.length) {
            loadExercise(exercises[newIndex]);
        }
    };

    // Imposta status esercizio a 'error'
    const markExerciseAsError = async () => {
        if (!selectedExercise) {
            setError('Nessun esercizio selezionato');
            return;
        }

        if (!window.confirm('Sei sicuro di voler segnalare questo esercizio come problematico? Lo status verrà impostato a "error".')) {
            return;
        }

        try {
            setSaving(true);
            // Includi tutti i campi necessari per evitare errori di null
            const response = await api.put(`/esercizi.php?id=${selectedExercise.id}`, {
                nome: selectedExercise.nome || formData.nome || '',
                nome_en: selectedExercise.nome_en || formData.nome_en || '',
                descrizione: selectedExercise.descrizione || formData.descrizione || '',
                descrizione_en: selectedExercise.descrizione_en || formData.descrizione_en || '',
                gruppo_muscolare: selectedExercise.gruppo_muscolare || formData.gruppo_muscolare || '',
                attrezzatura: selectedExercise.attrezzatura || formData.attrezzatura || '',
                immagine_nome: selectedExercise.immagine_nome || formData.immagine_nome || '',
                immagine_statica: selectedExercise.immagine_statica || formData.immagine_statica || '',
                primary_muscle_id: selectedExercise.primary_muscle_id || formData.primary_muscle_id || null,
                is_cardio: selectedExercise.is_cardio !== undefined ? selectedExercise.is_cardio : (formData.is_cardio !== undefined ? formData.is_cardio : 0),
                status: 'error'
            });

            if (response.data && response.data.success === false) {
                throw new Error(response.data.message || 'Errore nell\'aggiornamento dello status');
            }

            // Ricarica la lista degli esercizi (l'esercizio non dovrebbe più apparire)
            const updatedExercises = await fetchExercises();
            
            // Rimuovi l'esercizio dalla visualizzazione
            setSelectedExercise(null);
            setCurrentIndex(0);
            
            // Se ci sono altri esercizi nella lista, seleziona il primo
            if (updatedExercises && updatedExercises.length > 0) {
                loadExercise(updatedExercises[0]);
            }
            
            setError(null);
        } catch (err) {
            console.error('Errore nell\'aggiornamento dello status:', err);
            setError(err.response?.data?.message || 'Errore durante l\'aggiornamento dello status');
        } finally {
            setSaving(false);
        }
    };

    // Carica frame quando si passa alla tab frames
    useEffect(() => {
        if (activeTab === 'frames' && selectedExercise && frames.length === 0) {
            loadFrames();
        }
    }, [activeTab, selectedExercise]);
    
    // Aggiorna categoria quando i muscoli vengono caricati e c'è un esercizio selezionato
    useEffect(() => {
        if (selectedExercise && !musclesLoading && muscles.length > 0 && selectedExercise.primary_muscle_id) {
            // Trova sempre la categoria dal muscolo primario quando i muscoli sono disponibili
            const muscle = muscles.find(m => m.id === selectedExercise.primary_muscle_id);
            if (muscle && muscle.parent_category) {
                // Aggiorna la categoria se è diversa da quella attuale
                if (selectedCategory !== muscle.parent_category) {
                    setSelectedCategory(muscle.parent_category);
                }
            }
        }
    }, [muscles, musclesLoading, selectedExercise]);

    // Carica esercizi al mount
    useEffect(() => {
        fetchExercises();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600 dark:text-gray-400">Caricamento esercizi...</span>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="flex h-screen">
                {/* Sidebar sinistra - Lista esercizi */}
                <div className="w-80 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-y-auto">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Esercizi ({exercises.length})
                            </h3>
                            <button
                                onClick={fetchExercises}
                                className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                            >
                                Aggiorna
                            </button>
                        </div>
                        
                        {/* Ricerca per nome */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Cerca per nome
                            </label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Cerca esercizio..."
                                    className="w-full pl-10 pr-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        
                        {/* Filtro Attrezzatura */}
                        {availableEquipment.length > 0 && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Filtra per Attrezzatura
                                </label>
                                <select
                                    value={selectedEquipment}
                                    onChange={(e) => setSelectedEquipment(e.target.value)}
                                    className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Tutte le attrezzature</option>
                                    {availableEquipment.map((equipment) => (
                                        <option key={equipment} value={equipment}>
                                            {equipment}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {exercises.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                            <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>Nessun esercizio in attesa</p>
                        </div>
                    ) : (
                        <div className="p-2 space-y-2">
                            {exercises.map((exercise) => (
                                <div
                                    key={exercise.id}
                                    onClick={() => loadExercise(exercise)}
                                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                        selectedExercise?.id === exercise.id
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                                >
                                    <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">
                                        {exercise.nome_en || exercise.nome}
                                    </h4>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                        {exercise.gruppo_muscolare} • {exercise.attrezzatura}
                                    </p>
                                    {exercise.immagine_statica ? (
                                        <div className="mt-1 flex items-center text-xs text-green-600 dark:text-green-400">
                                            <CheckCircle className="h-3 w-3 mr-1" />
                                            Statico
                                        </div>
                                    ) : (
                                        <div className="mt-1 flex items-center text-xs text-yellow-600 dark:text-yellow-400">
                                            <AlertCircle className="h-3 w-3 mr-1" />
                                            Manca immagine
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Area principale */}
                <div className="flex-1 overflow-y-auto">
                    {!selectedExercise ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-gray-500 dark:text-gray-400">
                                <Image className="h-16 w-16 mx-auto mb-4 opacity-50" />
                                <p className="text-lg">Seleziona un esercizio per iniziare</p>
                            </div>
                        </div>
                    ) : (
                        <div className="p-6">
                            {/* Header con navigazione */}
                            <div className="mb-6 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={() => navigateExercise(-1)}
                                        disabled={currentIndex === 0}
                                        className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        <ChevronLeft className="h-5 w-5" />
                                    </button>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                        {currentIndex + 1} di {exercises.length}
                                    </span>
                                    <button
                                        onClick={() => navigateExercise(1)}
                                        disabled={currentIndex >= exercises.length - 1}
                                        className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        <ChevronRight className="h-5 w-5" />
                                    </button>
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white ml-4">
                                        {selectedExercise.nome_en || selectedExercise.nome}
                                    </h2>
                                </div>
                                
                                {modifiedFields.size > 0 && (
                                    <button
                                        onClick={saveExerciseChanges}
                                        disabled={saving}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {saving ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Salvataggio...
                                            </>
                                        ) : (
                                            <>
                                                <Save className="h-4 w-4" />
                                                Salva Modifiche ({modifiedFields.size})
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>

                            {error && (
                                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                    <div className="flex items-center">
                                        <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
                                        <span className="text-red-800 dark:text-red-200">{error}</span>
                                    </div>
                                </div>
                            )}

                            {/* Tabs */}
                            <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex gap-4">
                                    <button
                                        onClick={() => setActiveTab('editor')}
                                        className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                                            activeTab === 'editor'
                                                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                                                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                        }`}
                                    >
                                        Editor Dati
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('frames')}
                                        className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                                            activeTab === 'frames'
                                                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                                                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                        }`}
                                    >
                                        Selezione Frame
                                    </button>
                                </div>
                            </div>

                            {/* Tab Content */}
                            {activeTab === 'editor' ? (
                                <ExerciseDataEditor
                                    formData={formData}
                                    setFormData={setFormData}
                                    selectedCategory={selectedCategory}
                                    setSelectedCategory={setSelectedCategory}
                                    muscles={muscles}
                                    musclesLoading={musclesLoading}
                                    getMusclesByCategory={getMusclesByCategory}
                                    categories={categories}
                                    secondaryMuscles={secondaryMuscles}
                                    setSecondaryMuscles={setSecondaryMuscles}
                                    modifiedFields={modifiedFields}
                                    setModifiedFields={setModifiedFields}
                                    gifUrl={gifUrl}
                                    getApiBaseUrl={getApiBaseUrl}
                                    markExerciseAsError={markExerciseAsError}
                                    saving={saving}
                                />
                            ) : (
                                <FrameSelectionEditor
                                    selectedExercise={selectedExercise}
                                    frames={frames}
                                    framesLoading={framesLoading}
                                    selectedFrames={selectedFrames}
                                    setSelectedFrames={setSelectedFrames}
                                    toggleFrameSelection={toggleFrameSelection}
                                    frameCropAreas={frameCropAreas}
                                    setFrameCropAreas={setFrameCropAreas}
                                    previewUrl={previewUrl}
                                    setPreviewUrl={setPreviewUrl}
                                    generatingPreview={generatingPreview}
                                    generatePreview={generatePreview}
                                    generateAndSaveStaticImage={generateAndSaveStaticImage}
                                    saving={saving}
                                    gifUrl={gifUrl}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Componente per Editor Dati
const ExerciseDataEditor = ({
    formData,
    setFormData,
    selectedCategory,
    setSelectedCategory,
    muscles,
    musclesLoading,
    getMusclesByCategory,
    categories,
    secondaryMuscles,
    setSecondaryMuscles,
    modifiedFields,
    setModifiedFields,
    gifUrl,
    getApiBaseUrl,
    markExerciseAsError,
    saving
}) => {
    // Stato per il filtro categoria dei muscoli secondari
    const [secondaryMusclesFilter, setSecondaryMusclesFilter] = useState('');
    
    // Funzioni helper per gestire i muscoli secondari con activation_level
    const isMuscleSelected = (muscleId) => {
        return secondaryMuscles.some(m => m.muscle_id === muscleId);
    };
    
    const getMuscleActivationLevel = (muscleId) => {
        const muscle = secondaryMuscles.find(m => m.muscle_id === muscleId);
        return muscle ? muscle.activation_level : 'medium';
    };
    
    const toggleMuscleSelection = (muscleId, activationLevel = 'medium') => {
        if (isMuscleSelected(muscleId)) {
            // Rimuovi il muscolo
            setSecondaryMuscles(prev => prev.filter(m => m.muscle_id !== muscleId));
        } else {
            // Aggiungi il muscolo
            setSecondaryMuscles(prev => [...prev, { muscle_id: muscleId, activation_level: activationLevel }]);
        }
        setModifiedFields(prev => new Set([...prev, 'secondary_muscles']));
    };
    
    const updateMuscleActivationLevel = (muscleId, activationLevel) => {
        setSecondaryMuscles(prev => 
            prev.map(m => 
                m.muscle_id === muscleId 
                    ? { ...m, activation_level: activationLevel }
                    : m
            )
        );
        setModifiedFields(prev => new Set([...prev, 'secondary_muscles']));
    };
    
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
        
        setFormData(prev => ({ ...prev, [name]: newValue }));
        setModifiedFields(prev => new Set([...prev, name]));
    };

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        setFormData(prev => ({ ...prev, primary_muscle_id: null }));
        setModifiedFields(prev => new Set([...prev, 'primary_muscle_id']));
    };

    const handlePrimaryMuscleChange = (muscleId) => {
        const muscle = muscles.find(m => m.id === parseInt(muscleId));
        setFormData(prev => ({
            ...prev,
            primary_muscle_id: muscleId ? parseInt(muscleId) : null,
            gruppo_muscolare: muscle ? muscle.parent_category : prev.gruppo_muscolare
        }));
        setModifiedFields(prev => new Set([...prev, 'primary_muscle_id', 'gruppo_muscolare']));
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Colonna sinistra - GIF e info base */}
            <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Anteprima GIF</h3>
                    {gifUrl ? (
                        <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-4 flex items-center justify-center min-h-[300px]">
                            <img
                                src={gifUrl}
                                alt="Exercise GIF"
                                className="max-w-full max-h-[400px] object-contain"
                            />
                        </div>
                    ) : (
                        <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-4 flex items-center justify-center min-h-[300px] text-gray-500 dark:text-gray-400">
                            Nessuna GIF disponibile
                        </div>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Informazioni Base</h3>
                    <div className="space-y-3 text-sm">
                        <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Nome Immagine:</span>
                            <p className="text-gray-600 dark:text-gray-400">{formData.immagine_nome || 'N/A'}</p>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Immagine Statica:</span>
                            {formData.immagine_statica ? (
                                <div className="mt-2">
                                    <p className="text-gray-600 dark:text-gray-400 mb-2">{formData.immagine_statica}</p>
                                    <img
                                        src={`${getApiBaseUrl()}/serve_image.php?filename=${encodeURIComponent(formData.immagine_statica)}&type=image`}
                                        alt="Immagine statica"
                                        className="max-w-full h-auto rounded-lg border border-gray-300 dark:border-gray-600"
                                        style={{ maxHeight: '200px' }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'block';
                                        }}
                                    />
                                    <p className="text-red-500 text-sm mt-1" style={{ display: 'none' }}>
                                        Immagine non trovata
                                    </p>
                                </div>
                            ) : (
                                <p className="text-gray-600 dark:text-gray-400">Non generata</p>
                            )}
                        </div>
                    </div>
                    
                    {/* Pulsante Segnala Problema */}
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            onClick={markExerciseAsError}
                            disabled={saving}
                            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Aggiornamento...
                                </>
                            ) : (
                                <>
                                    <AlertCircle className="h-4 w-4" />
                                    Segnala Esercizio con Problema
                                </>
                            )}
                        </button>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                            Imposta lo status a "error" per segnalare che questo esercizio ha un problema
                        </p>
                    </div>
                </div>
            </div>

            {/* Colonna destra - Form */}
            <div className="space-y-4">
                {/* Nome */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nome (Italiano) {modifiedFields.has('nome') && <span className="text-yellow-600">*</span>}
                    </label>
                    <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* Nome EN */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nome (Inglese) {modifiedFields.has('nome_en') && <span className="text-yellow-600">*</span>}
                    </label>
                    <input
                        type="text"
                        name="nome_en"
                        value={formData.nome_en}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* Categoria Muscolare */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Categoria Muscolare {modifiedFields.has('gruppo_muscolare') && <span className="text-yellow-600">*</span>}
                    </label>
                    <select
                        value={selectedCategory}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Seleziona categoria</option>
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>

                {/* Muscolo Primario */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Muscolo Primario {modifiedFields.has('primary_muscle_id') && <span className="text-yellow-600">*</span>}
                    </label>
                    <select
                        value={formData.primary_muscle_id || ''}
                        onChange={(e) => handlePrimaryMuscleChange(e.target.value)}
                        disabled={!selectedCategory || musclesLoading}
                        className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        <option value="">
                            {musclesLoading ? 'Caricamento...' : 'Seleziona muscolo primario'}
                        </option>
                        {selectedCategory && getMusclesByCategory(selectedCategory).map(muscle => (
                            <option key={muscle.id} value={muscle.id}>
                                {muscle.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Muscoli Secondari */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Muscoli Secondari {modifiedFields.has('secondary_muscles') && <span className="text-yellow-600">*</span>}
                    </label>
                    
                    {/* Filtri per categoria */}
                    <div className="mb-3 flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => setSecondaryMusclesFilter('')}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                                secondaryMusclesFilter === ''
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                            }`}
                        >
                            Tutti
                        </button>
                        {categories.map(cat => (
                            <button
                                key={cat}
                                type="button"
                                onClick={() => setSecondaryMusclesFilter(cat)}
                                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                                    secondaryMusclesFilter === cat
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                                }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                    
                    <div className="max-h-64 overflow-y-auto border rounded-lg p-2 bg-white dark:bg-gray-700">
                        {musclesLoading ? (
                            <div className="p-4 text-center text-gray-500">Caricamento muscoli...</div>
                        ) : (
                            <>
                                {secondaryMusclesFilter ? (
                                    // Mostra solo i muscoli della categoria filtrata
                                    <>
                                        <div className="text-xs font-bold uppercase text-gray-500 dark:text-gray-400 mb-2 sticky top-0 bg-white dark:bg-gray-700 py-1">
                                            {secondaryMusclesFilter}
                                        </div>
                                        {getMusclesByCategory(secondaryMusclesFilter).map(muscle => {
                                            const isSelected = isMuscleSelected(muscle.id);
                                            const activationLevel = getMuscleActivationLevel(muscle.id);
                                            const activationLevelLabels = {
                                                low: 'Basso',
                                                medium: 'Medio',
                                                high: 'Alto'
                                            };
                                            return (
                                                <div key={muscle.id} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded">
                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleMuscleSelection(muscle.id, activationLevel)}
                                                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                        />
                                                        <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">{muscle.name}</span>
                                                        {isSelected && (
                                                            <>
                                                                <select
                                                                    value={activationLevel}
                                                                    onChange={(e) => updateMuscleActivationLevel(muscle.id, e.target.value)}
                                                                    className="text-xs p-1 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <option value="low">Basso</option>
                                                                    <option value="medium">Medio</option>
                                                                    <option value="high">Alto</option>
                                                                </select>
                                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                    ({activationLevelLabels[activationLevel]})
                                                                </span>
                                                            </>
                                                        )}
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </>
                                ) : (
                                    // Mostra tutti i muscoli raggruppati per categoria
                                    // I muscoli già selezionati compaiono tutti insieme all'inizio
                                    <>
                                        {/* Sezione Muscoli Selezionati - mostrata per prima */}
                                        {(() => {
                                            // Raccogli tutti i muscoli selezionati da tutte le categorie con activation_level
                                            const allSelectedMuscles = [];
                                            categories.forEach(cat => {
                                                const musclesInCategory = getMusclesByCategory(cat);
                                                musclesInCategory.forEach(muscle => {
                                                    if (isMuscleSelected(muscle.id)) {
                                                        const activationLevel = getMuscleActivationLevel(muscle.id);
                                                        allSelectedMuscles.push({ ...muscle, category: cat, activation_level: activationLevel });
                                                    }
                                                });
                                            });
                                            
                                            if (allSelectedMuscles.length > 0) {
                                                return (
                                                    <div className="mb-6">
                                                        <div className="text-xs font-bold uppercase text-blue-600 dark:text-blue-400 mb-2 sticky top-0 bg-white dark:bg-gray-700 py-1">
                                                            Muscoli Selezionati ({allSelectedMuscles.length})
                                                        </div>
                                                        {allSelectedMuscles.map(muscle => {
                                                            const activationLevelLabels = {
                                                                low: 'Basso',
                                                                medium: 'Medio',
                                                                high: 'Alto'
                                                            };
                                                            return (
                                                                <div
                                                                    key={muscle.id}
                                                                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded bg-blue-50 dark:bg-blue-900/20 mb-1"
                                                                >
                                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={true}
                                                                            onChange={() => toggleMuscleSelection(muscle.id)}
                                                                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                                        />
                                                                        <span className="text-sm text-gray-700 dark:text-gray-300 font-medium flex-1">{muscle.name}</span>
                                                                        <span className="text-xs text-gray-500 dark:text-gray-400">{muscle.category}</span>
                                                                        <select
                                                                            value={muscle.activation_level}
                                                                            onChange={(e) => updateMuscleActivationLevel(muscle.id, e.target.value)}
                                                                            className="text-xs p-1 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <option value="low">Basso</option>
                                                                            <option value="medium">Medio</option>
                                                                            <option value="high">Alto</option>
                                                                        </select>
                                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                            ({activationLevelLabels[muscle.activation_level]})
                                                                        </span>
                                                                    </label>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                        
                                        {/* Mostra tutte le categorie con solo i muscoli non selezionati */}
                                        {categories.map(cat => {
                                            const musclesInCategory = getMusclesByCategory(cat);
                                            if (musclesInCategory.length === 0) return null;
                                            
                                            // Mostra solo i muscoli non selezionati
                                            const unselectedMuscles = musclesInCategory.filter(muscle => !isMuscleSelected(muscle.id));
                                            
                                            // Se tutti i muscoli di questa categoria sono selezionati, non mostrare la categoria
                                            if (unselectedMuscles.length === 0) return null;
                                            
                                            return (
                                                <div key={cat} className="mb-4">
                                                    <div className="text-xs font-bold uppercase text-gray-500 dark:text-gray-400 mb-2 sticky top-0 bg-white dark:bg-gray-700 py-1">
                                                        {cat}
                                                    </div>
                                                    {unselectedMuscles.map(muscle => {
                                                        return (
                                                            <label
                                                                key={muscle.id}
                                                                className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={false}
                                                                    onChange={() => toggleMuscleSelection(muscle.id, 'medium')}
                                                                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                                />
                                                                <span className="text-sm text-gray-700 dark:text-gray-300">{muscle.name}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    {secondaryMuscles.length > 0 && (
                        <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            {secondaryMuscles.length} {secondaryMuscles.length === 1 ? 'muscolo selezionato' : 'muscoli selezionati'}
                        </div>
                    )}
                </div>

                {/* Attrezzatura */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Attrezzatura {modifiedFields.has('attrezzatura') && <span className="text-yellow-600">*</span>}
                    </label>
                    <input
                        type="text"
                        name="attrezzatura"
                        value={formData.attrezzatura}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                        placeholder="es. Bilanciere, Manubri, Corpo libero"
                    />
                </div>

                {/* Is Cardio */}
                <div>
                    <label className="flex items-center space-x-3">
                        <input
                            type="checkbox"
                            name="is_cardio"
                            checked={!!formData.is_cardio}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-gray-700 dark:text-gray-300">Esercizio Cardio</span>
                    </label>
                </div>

                {/* Descrizione */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Descrizione (Italiano) {modifiedFields.has('descrizione') && <span className="text-yellow-600">*</span>}
                    </label>
                    <textarea
                        name="descrizione"
                        value={formData.descrizione}
                        onChange={handleInputChange}
                        rows="4"
                        className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* Descrizione EN */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Descrizione (Inglese) {modifiedFields.has('descrizione_en') && <span className="text-yellow-600">*</span>}
                    </label>
                    <textarea
                        name="descrizione_en"
                        value={formData.descrizione_en}
                        onChange={handleInputChange}
                        rows="4"
                        className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            </div>
        </div>
    );
};

// Componente per Selezione Frame
const FrameSelectionEditor = ({
    selectedExercise,
    frames,
    framesLoading,
    selectedFrames,
    setSelectedFrames,
    toggleFrameSelection,
    frameCropAreas,
    setFrameCropAreas,
    previewUrl,
    setPreviewUrl,
    generatingPreview,
    generatePreview,
    generateAndSaveStaticImage,
    saving,
    gifUrl
}) => {
    // Funzione per tornare indietro e riselezionare i frame
    const handleResetPreview = () => {
        setPreviewUrl(null);
    };

    return (
        <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Genera Immagine Statica
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    Seleziona 2 o 3 frame dalla GIF, seleziona l'area per ogni frame, poi genera l'immagine statica
                </p>

                {/* Se c'è un'anteprima, mostra solo quella con il pulsante per tornare indietro */}
                {previewUrl ? (
                    <div className="space-y-4">
                        <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                Anteprima Immagine Statica
                            </h4>
                            <div className="bg-white dark:bg-gray-800 rounded p-4 flex items-center justify-center">
                                <img
                                    src={previewUrl}
                                    alt="Preview"
                                    className="max-w-full max-h-[400px] object-contain"
                                />
                            </div>
                            <div className="mt-4 flex gap-4">
                                <button
                                    onClick={handleResetPreview}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    Torna Indietro e Riseleziona Frame
                                </button>
                                <button
                                    onClick={generateAndSaveStaticImage}
                                    disabled={saving}
                                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Salvataggio...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="h-4 w-4" />
                                            Genera e Salva Immagine Statica
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Anteprima GIF - mostrata solo se non c'è anteprima */}
                        {gifUrl && (
                            <div className="mb-6 bg-gray-100 dark:bg-gray-900 rounded-lg p-4">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">GIF Originale</h4>
                                <div className="flex items-center justify-center bg-white dark:bg-gray-800 rounded p-4">
                                    <img
                                        src={gifUrl}
                                        alt="Exercise GIF"
                                        className="max-w-full max-h-[200px] object-contain"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Caricamento frame */}
                        {framesLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                                <span className="ml-2 text-gray-600 dark:text-gray-400">Caricamento frame...</span>
                            </div>
                        ) : frames.length === 0 ? (
                            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                                <p>Nessun frame disponibile</p>
                            </div>
                        ) : (
                            <>
                                {/* Griglia Frame */}
                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Seleziona Frame ({selectedFrames.length}/3)
                                        </h4>
                                        <button
                                            onClick={() => {
                                                if (frames.length >= 2) {
                                                    const firstFrameIndex = frames[0].index;
                                                    const middleFrameIndex = frames[Math.floor(frames.length / 2)].index;
                                                    setSelectedFrames([firstFrameIndex, middleFrameIndex].sort((a, b) => a - b));
                                                }
                                            }}
                                            className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                                        >
                                            <CheckCircle className="h-3 w-3" />
                                            Selezione Rapida (1° + Metà)
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-8 gap-3 max-h-[400px] overflow-y-auto p-2">
                                        {frames.map((frame) => {
                                            const isSelected = selectedFrames.includes(frame.index);
                                            return (
                                                <div
                                                    key={frame.index}
                                                    onClick={() => toggleFrameSelection(frame.index)}
                                                    className={`border-2 rounded-lg p-2 cursor-pointer transition-all ${
                                                        isSelected
                                                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                                                            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
                                                    }`}
                                                >
                                                    <img
                                                        src={frame.url}
                                                        alt={`Frame ${frame.index}`}
                                                        className="w-full h-auto rounded"
                                                    />
                                                    <p className="text-xs text-center mt-1 text-gray-600 dark:text-gray-400">
                                                        {frame.index}
                                                    </p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Frame Selezionati con Crop */}
                                {selectedFrames.length >= 2 && (
                                    <div className="space-y-4">
                                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Frame Selezionati - Seleziona Area (Clicca e Trascina)
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {selectedFrames.map((frameIndex) => {
                                                const frame = frames.find(f => f.index === frameIndex);
                                                if (!frame) return null;
                                                
                                                return (
                                                    <FrameCropArea
                                                        key={frameIndex}
                                                        frame={frame}
                                                        frameIndex={frameIndex}
                                                        cropArea={frameCropAreas[frameIndex]}
                                                        setCropArea={(crop) => {
                                                            setFrameCropAreas(prev => ({
                                                                ...prev,
                                                                [frameIndex]: crop
                                                            }));
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>

                                        {/* Controlli */}
                                        <div className="mt-6">
                                            <button
                                                onClick={generatePreview}
                                                disabled={generatingPreview || selectedFrames.length < 2}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                            >
                                                {generatingPreview ? (
                                                    <>
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                        Generazione...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Image className="h-4 w-4" />
                                                        Genera Anteprima
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

// Componente per Crop Area
const FrameCropArea = ({ frame, frameIndex, cropArea, setCropArea }) => {
    const containerRef = React.useRef(null);
    const imgRef = React.useRef(null);
    const cropRef = React.useRef(null);
    const [isSelecting, setIsSelecting] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
    const [imgOffset, setImgOffset] = useState({ x: 0, y: 0 });
    const autoCropCalculated = React.useRef(false);

    // Funzione per calcolare automaticamente l'area di crop escludendo lo sfondo bianco
    const calculateAutoCrop = React.useCallback((img) => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Imposta dimensioni canvas alle dimensioni reali dell'immagine
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            
            // Disegna l'immagine sul canvas
            ctx.drawImage(img, 0, 0);
            
            try {
                // Ottieni i pixel dell'immagine
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const data = imageData.data;
                
                // Soglia per determinare se un pixel è "bianco" (considera anche quasi-bianco)
                const WHITE_THRESHOLD = 240; // Valori RGB sopra questa soglia sono considerati bianchi
                
                let minX = canvas.width;
                let maxX = 0;
                let minY = canvas.height;
                let maxY = 0;
                
                // Analizza i pixel per trovare i bordi non-bianchi
                for (let y = 0; y < canvas.height; y++) {
                    for (let x = 0; x < canvas.width; x++) {
                        const index = (y * canvas.width + x) * 4;
                        const r = data[index];
                        const g = data[index + 1];
                        const b = data[index + 2];
                        const a = data[index + 3]; // alpha
                        
                        // Ignora pixel trasparenti
                        if (a < 128) continue;
                        
                        // Verifica se il pixel NON è bianco
                        if (r < WHITE_THRESHOLD || g < WHITE_THRESHOLD || b < WHITE_THRESHOLD) {
                            // Aggiorna i bordi
                            if (x < minX) minX = x;
                            if (x > maxX) maxX = x;
                            if (y < minY) minY = y;
                            if (y > maxY) maxY = y;
                        }
                    }
                }
                
                // Verifica che abbiamo trovato un'area valida
                if (minX < maxX && minY < maxY) {
                    // Calcola le coordinate relative all'immagine visualizzata (non alle dimensioni naturali)
                    // Dobbiamo scalare dalle dimensioni naturali a quelle visualizzate
                    const scaleX = img.width / (img.naturalWidth || img.width);
                    const scaleY = img.height / (img.naturalHeight || img.height);
                    
                    const cropX = Math.round(minX * scaleX);
                    const cropY = Math.round(minY * scaleY);
                    const cropWidth = Math.round((maxX - minX + 1) * scaleX);
                    const cropHeight = Math.round((maxY - minY + 1) * scaleY);
                    
                    resolve({
                        x: Math.max(0, cropX),
                        y: Math.max(0, cropY),
                        width: Math.min(cropWidth, img.width - cropX),
                        height: Math.min(cropHeight, img.height - cropY)
                    });
                } else {
                    // Se non troviamo un'area valida, usa l'intera immagine
                    resolve({
                        x: 0,
                        y: 0,
                        width: img.width,
                        height: img.height
                    });
                }
            } catch (error) {
                console.warn('Errore nel calcolo automatico del crop:', error);
                // In caso di errore, usa l'intera immagine
                resolve({
                    x: 0,
                    y: 0,
                    width: img.width,
                    height: img.height
                });
            }
        });
    }, []);

    const handleMouseDown = (e) => {
        if (!containerRef.current || !imgRef.current) return;
        
        e.preventDefault();
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        setStartPos({ x, y });
        setCurrentPos({ x, y });
        setIsSelecting(true);
    };

    const handleMouseMove = (e) => {
        if (!isSelecting || !containerRef.current) return;
        
        e.preventDefault();
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        setCurrentPos({ x, y });
    };

    const handleMouseUp = () => {
        if (!isSelecting || !imgRef.current || !containerRef.current) return;
        
        setIsSelecting(false);
        
        // Calcola area di crop - coordinate relative all'immagine visualizzata
        const img = imgRef.current;
        const container = containerRef.current;
        
        // Coordinate dello schermo relative al container
        const x = Math.min(startPos.x, currentPos.x);
        const y = Math.min(startPos.y, currentPos.y);
        const width = Math.abs(currentPos.x - startPos.x);
        const height = Math.abs(currentPos.y - startPos.y);
        
        // Converti coordinate schermo in coordinate relative all'immagine visualizzata
        const imgRect = img.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        // Offset dell'immagine rispetto al container
        const imgOffsetX = imgRect.left - containerRect.left;
        const imgOffsetY = imgRect.top - containerRect.top;
        
        // Coordinate relative all'immagine visualizzata (img.width x img.height)
        // NON scaliamo a naturalWidth/naturalHeight - il PHP lo farà
        const imgX = Math.max(0, Math.min(x - imgOffsetX, img.width));
        const imgY = Math.max(0, Math.min(y - imgOffsetY, img.height));
        const imgWidth = Math.max(10, Math.min(width, img.width - imgX));
        const imgHeight = Math.max(10, Math.min(height, img.height - imgY));
        
        if (imgWidth > 10 && imgHeight > 10) {
            // IMPORTANTE: Invia coordinate relative all'immagine visualizzata (circa 250px)
            // Il PHP le scalerà alle dimensioni originali usando scaleFactor = originalWidth / 250
            setCropArea({
                x: Math.round(imgX),
                y: Math.round(imgY),
                width: Math.round(imgWidth),
                height: Math.round(imgHeight)
            });
        }
    };

    React.useEffect(() => {
        if (isSelecting) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isSelecting, startPos, currentPos]);

    // Reset auto-crop quando cambia il frame
    React.useEffect(() => {
        autoCropCalculated.current = false;
    }, [frameIndex]);

    // Calcola offset dell'immagine rispetto al container e auto-crop
    React.useEffect(() => {
        const updateOffset = () => {
            if (imgRef.current && containerRef.current) {
                const imgRect = imgRef.current.getBoundingClientRect();
                const containerRect = containerRef.current.getBoundingClientRect();
                setImgOffset({
                    x: imgRect.left - containerRect.left,
                    y: imgRect.top - containerRect.top
                });
            }
        };
        
        const handleImageLoad = async () => {
            updateOffset();
            
            // Calcola automaticamente il crop se non è già stato impostato manualmente
            if (!cropArea && imgRef.current && !autoCropCalculated.current && imgRef.current.complete) {
                // Verifica che l'immagine abbia dimensioni valide
                if ((imgRef.current.naturalWidth || imgRef.current.width) > 0 && 
                    (imgRef.current.naturalHeight || imgRef.current.height) > 0) {
                    autoCropCalculated.current = true;
                    try {
                        const autoCrop = await calculateAutoCrop(imgRef.current);
                        setCropArea(autoCrop);
                    } catch (error) {
                        console.warn('Errore nel calcolo automatico del crop:', error);
                        autoCropCalculated.current = false; // Permetti di riprovare
                    }
                }
            }
        };
        
        updateOffset();
        
        // Aggiorna quando l'immagine viene caricata
        if (imgRef.current) {
            if (imgRef.current.complete && imgRef.current.naturalWidth > 0) {
                handleImageLoad();
            } else {
                imgRef.current.addEventListener('load', handleImageLoad);
            }
        }
        
        // Aggiorna quando la finestra viene ridimensionata
        window.addEventListener('resize', updateOffset);
        
        return () => {
            if (imgRef.current) {
                imgRef.current.removeEventListener('load', handleImageLoad);
            }
            window.removeEventListener('resize', updateOffset);
        };
    }, [frame, cropArea, calculateAutoCrop, setCropArea, frameIndex]);

    return (
        <div className="space-y-2">
            <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400">
                Frame {frameIndex}
            </h5>
            <div
                ref={containerRef}
                className="relative border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-900"
                onMouseDown={handleMouseDown}
            >
                <img
                    ref={imgRef}
                    src={frame.url}
                    alt={`Frame ${frameIndex}`}
                    className="cursor-crosshair select-none"
                    style={{ maxWidth: '250px', maxHeight: '250px', width: 'auto', height: 'auto' }}
                    draggable={false}
                />
                {isSelecting && (
                    <div
                        ref={cropRef}
                        className="absolute border-2 border-dashed border-blue-500 bg-blue-500/10 pointer-events-none"
                        style={{
                            left: Math.min(startPos.x, currentPos.x),
                            top: Math.min(startPos.y, currentPos.y),
                            width: Math.abs(currentPos.x - startPos.x),
                            height: Math.abs(currentPos.y - startPos.y)
                        }}
                    />
                )}
                {cropArea && !isSelecting && imgRef.current && (
                    <div
                        className="absolute border-2 border-green-500 bg-green-500/10 pointer-events-none"
                        style={{
                            left: (() => {
                                // Converti coordinate immagine (img.width) a coordinate schermo
                                const img = imgRef.current;
                                const imgRect = img.getBoundingClientRect();
                                const containerRect = containerRef.current.getBoundingClientRect();
                                const imgOffsetX = imgRect.left - containerRect.left;
                                const scaleX = imgRect.width / img.width;
                                return imgOffsetX + (cropArea.x * scaleX);
                            })(),
                            top: (() => {
                                // Converti coordinate immagine (img.height) a coordinate schermo
                                const img = imgRef.current;
                                const imgRect = img.getBoundingClientRect();
                                const containerRect = containerRef.current.getBoundingClientRect();
                                const imgOffsetY = imgRect.top - containerRect.top;
                                const scaleY = imgRect.height / img.height;
                                return imgOffsetY + (cropArea.y * scaleY);
                            })(),
                            width: (() => {
                                const img = imgRef.current;
                                const imgRect = img.getBoundingClientRect();
                                const scaleX = imgRect.width / img.width;
                                return cropArea.width * scaleX;
                            })(),
                            height: (() => {
                                const img = imgRef.current;
                                const imgRect = img.getBoundingClientRect();
                                const scaleY = imgRect.height / img.height;
                                return cropArea.height * scaleY;
                            })()
                        }}
                    />
                )}
            </div>
            {cropArea && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Crop: {cropArea.width}x{cropArea.height}px
                </p>
            )}
        </div>
    );
};

export default ExerciseImageEditor;
